import apiClient from "../apiClient";

import type { LoginCode, SSEData, UserInfo, UserToken } from "#/entity";

export interface SignInReq {
	username: string;
	password: string;
}

export interface SignUpReq extends SignInReq {
	email: string;
}
export type SignInRes = UserToken & { user: UserInfo };

export enum UserApi {
	SignIn = "/staff/login/pwd",
	SignUp = "/auth/signup",
	Logout = "/auth/logout",
	Refresh = "/auth/refresh",
	User = "/user",
	GetCode = "login/code",
	CodeEvent = "login/code/event",
}

export enum StoreApi {
	CheckBrandUser = "store/sku/check",
	BillSummary = "store/bill/summary",
	PageProduct = "/store/product/page",
	DeleteProduct = "/store/product/delete",
	SaveProduct = "/store/product/save",
	PageSku = "/store/sku/page",
	FirstPush = "/store/sku/first_push",
	TreeChannel = "/mall/TreeChannel",
	Calculate = "/store/sku/calculate",
	EditPush = "/store/sku/edit_push",
	Unpublish = "/store/sku/unpublish",
	Publish = "/store/sku/publish",
	DeleteSku = "/store/sku/delete",
}

// 商品分类
export enum ClassifyApi {
	CreateClassify = "/category/create",
	UpdateClassify = "/category/update",
	DeleteClassify = "/category/delete",
	PageClassify = "/category/list",
}

//订单
export enum OrderApi {
	SearchOrder = "/order/SearchOrder",
	ExpressComList = "/order/ExpressComList",
	DeliverOrder = "/order/DeliverOrder",
	DeliveryBatch = "/order/DeliveryBatch",
	UpdateDeliverInfo = "/order/UpdateDeliverInfo",
}

//订单
export enum StoreOrderApi {
	Page = "/store/order/page",
	Delivery = "/store/order/delivery",
	Count = "/store/order/count",
	DeliveryCompany = "/store/order/delivery/company",
	DeliveryUpdate = "/store/order/delivery/update",
	DeliveryBatch = "/store/order/delivery/batch",
}

// 结算
export enum SettleApi {
	SearchBill = "/store/bill/page",
	BillSummary = "/store/bill/summary",
	WithdrawApply = "/store/withdraw/submit",
	PageWithdrawRecord = "/store/withdraw/page",
}

const signin = (data: SignInReq) => apiClient.post<SignInRes>({ url: UserApi.SignIn, data });
const signup = (data: SignUpReq) => apiClient.post<SignInRes>({ url: UserApi.SignUp, data });
const logout = () => apiClient.get({ url: UserApi.Logout });
const findById = (id: string) => apiClient.get<UserInfo[]>({ url: `${UserApi.User}/${id}` });
const getCode = () => apiClient.get<{ code: number; data: LoginCode; msg: string }>({ url: UserApi.GetCode });
const checkBrandUser = () => apiClient.get<{ code: number; data: { isBrandUser: boolean; tags?: string[] }; msg: string }>({ url: StoreApi.CheckBrandUser });
const getStoreBillSummary = () =>
	apiClient.get<{ code: number; data: { name?: string; avatar?: string; sname?: string }; msg: string }>({ url: StoreApi.BillSummary });
const searchOrder = (data: any) => apiClient.get<any>({ url: OrderApi.SearchOrder, params: data });
const expressComList = () => apiClient.get<any>({ url: OrderApi.ExpressComList });
const deliverOrder = (data: any) => apiClient.post<any>({ url: OrderApi.DeliverOrder, data });
const storeOrderPage = (data: any) => apiClient.get<any>({ url: StoreOrderApi.Page, params: data });
const storeOrderDelivery = (data: any) => apiClient.post<any>({ url: StoreOrderApi.Delivery, data });
const storeOrderCount = () => apiClient.get<any>({ url: StoreOrderApi.Count });
const storeOrderDeliveryCompany = () => apiClient.get<any>({ url: StoreOrderApi.DeliveryCompany });
const storeOrderDeliveryUpdate = (data: any) => apiClient.post<any>({ url: StoreOrderApi.DeliveryUpdate, data });
const storeOrderDeliveryBatch = (data: any) => apiClient.post<any>({ url: StoreOrderApi.DeliveryBatch, data });
const searchBill = (data: any) => apiClient.get<any>({ url: SettleApi.SearchBill, params: data });
const billSummary = () => apiClient.get<any>({ url: SettleApi.BillSummary });
const withdrawApply = (data: any) => apiClient.post<any>({ url: SettleApi.WithdrawApply, data });
const pageWithdrawRecord = (data: any) => apiClient.get<any>({ url: SettleApi.PageWithdrawRecord, params: data });
const deliveryBatch = (data: any) => apiClient.post<any>({ url: OrderApi.DeliveryBatch, data });
const updateDeliverInfo = (data: any) => apiClient.post<any>({ url: OrderApi.UpdateDeliverInfo, data });
const createClassify = (data: any) => apiClient.post<any>({ url: ClassifyApi.CreateClassify, data });
const updateClassify = (data: any) => apiClient.post<any>({ url: ClassifyApi.UpdateClassify, data });
const deleteClassify = (data: any) => apiClient.post<any>({ url: ClassifyApi.DeleteClassify, data });
const pageClassify = () => apiClient.get<any>({ url: ClassifyApi.PageClassify });
const pageProduct = (data: any) => apiClient.get<any>({ url: StoreApi.PageProduct, params: data });
const deleteProduct = (data: any) => apiClient.post<any>({ url: StoreApi.DeleteProduct, data });
const saveProduct = (data: any) => apiClient.post<any>({ url: StoreApi.SaveProduct, data });
const pageSku = (data: any) => apiClient.get<any>({ url: StoreApi.PageSku, params: data });
const firstPush = (data: any) => apiClient.post<any>({ url: StoreApi.FirstPush, data });
const treeChannel = (data: any) => apiClient.get<any>({ url: StoreApi.TreeChannel, params: data });
const calculate = (data: any) => apiClient.post<any>({ url: StoreApi.Calculate, data });
const editPush = (data: any) => apiClient.post<any>({ url: StoreApi.EditPush, data });
const unpublish = (data: any) => apiClient.post<any>({ url: StoreApi.Unpublish, data });
const publish = (data: any) => apiClient.post<any>({ url: StoreApi.Publish, data });
const deleteSku = (data: any) => apiClient.post<any>({ url: StoreApi.DeleteSku, data });

// SSE连接方法
const createSSEConnection = (code: string, onMessage: (message: SSEData) => void, onError?: (error: Event) => void) => {
	const eventSource = new EventSource(`${import.meta.env.VITE_APP_BASE_API}/${UserApi.CodeEvent}?code=${code}`);

	eventSource.addEventListener("code.login", (event) => {
		try {
			const message: SSEData = JSON.parse((event as MessageEvent).data);
			onMessage(message);
		} catch (error) {
			console.error("SSE消息解析失败:", error);
		}
	});

	eventSource.onerror = (error) => {
		console.error("SSE连接错误:", error);
		onError?.(error);
	};

	return eventSource;
};

export default {
	signin,
	signup,
	findById,
	logout,
	getCode,
	checkBrandUser,
	getStoreBillSummary,
	createSSEConnection,
	searchOrder,
	expressComList,
	deliverOrder,
	storeOrderPage,
	storeOrderDelivery,
	storeOrderCount,
	storeOrderDeliveryCompany,
	storeOrderDeliveryUpdate,
	storeOrderDeliveryBatch,
	searchBill,
	billSummary,
	withdrawApply,
	pageWithdrawRecord,
	deliveryBatch,
	updateDeliverInfo,
	createClassify,
	updateClassify,
	deleteClassify,
	pageClassify,
	pageProduct,
	deleteProduct,
	saveProduct,
	pageSku,
	firstPush,
	treeChannel,
	calculate,
	editPush,
	unpublish,
	publish,
	deleteSku,
};
